import React from "react";
import {
  Alert,
  Breadcrumbs,
  Button,
  Chip,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  fetchVehicleEngines,
  fetchVehicleMakes,
  fetchVehicleModels,
  fetchVehicleVariants,
} from "../data/vehicleCatalog";

const STEP_CONFIG = [
  { key: "make", label: "Hersteller" },
  { key: "model", label: "Modell" },
  { key: "variant", label: "Variante" },
  { key: "engine", label: "Motor" },
];

function formatMakeSecondary(option) {
  return option.country ? `Marke aus ${option.country}` : null;
}

function formatModelSecondary(option) {
  const details = [option.description, option.years].filter(Boolean);
  return details.join(" · ") || null;
}

function formatVariantSecondary(option) {
  const details = [option.body, option.years].filter(Boolean);
  return details.join(" · ") || null;
}

function formatEngineSecondary(option) {
  const details = [];
  if (option.fuel) {
    details.push(option.fuel);
  }
  const power = [
    option.powerKw != null ? `${option.powerKw} kW` : null,
    option.powerPs != null ? `${option.powerPs} PS` : null,
  ].filter(Boolean);
  if (power.length > 0) {
    details.push(power.join(" / "));
  }
  if (option.displacement != null) {
    details.push(`${option.displacement} ccm`);
  }
  return details.join(" · ") || null;
}

const SECONDARY_FORMATTERS = [
  formatMakeSecondary,
  formatModelSecondary,
  formatVariantSecondary,
  formatEngineSecondary,
];

const EMPTY_MESSAGES = [
  "Keine Hersteller im Katalog hinterlegt.",
  "Für diesen Hersteller sind keine Modelle gepflegt.",
  "Für dieses Modell wurden keine Varianten gefunden.",
  "Für diese Variante sind keine Motoren hinterlegt.",
];

export default function VehicleSelector({ onVehicleSelected, onSelectionReset }) {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [selectedMake, setSelectedMake] = React.useState(null);
  const [selectedModel, setSelectedModel] = React.useState(null);
  const [selectedVariant, setSelectedVariant] = React.useState(null);
  const [selectedEngine, setSelectedEngine] = React.useState(null);

  const [makeState, setMakeState] = React.useState({ options: [], loading: false, error: null });
  const [modelState, setModelState] = React.useState({ options: [], loading: false, error: null });
  const [variantState, setVariantState] = React.useState({ options: [], loading: false, error: null });
  const [engineState, setEngineState] = React.useState({ options: [], loading: false, error: null });

  React.useEffect(() => {
    let active = true;
    setMakeState((prev) => ({ ...prev, loading: true, error: null }));
    fetchVehicleMakes()
      .then((options) => {
        if (!active) {
          return;
        }
        setMakeState({ options, loading: false, error: null });
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        setMakeState({ options: [], loading: false, error });
      });
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (!selectedMake) {
      setModelState({ options: [], loading: false, error: null });
      return;
    }

    let active = true;
    setModelState({ options: [], loading: true, error: null });
    fetchVehicleModels(selectedMake.code)
      .then((options) => {
        if (!active) {
          return;
        }
        setModelState({ options, loading: false, error: null });
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        setModelState({ options: [], loading: false, error });
      });

    return () => {
      active = false;
    };
  }, [selectedMake]);

  React.useEffect(() => {
    if (!selectedMake || !selectedModel) {
      setVariantState({ options: [], loading: false, error: null });
      return;
    }

    let active = true;
    setVariantState({ options: [], loading: true, error: null });
    fetchVehicleVariants(selectedMake.code, selectedModel.code)
      .then((options) => {
        if (!active) {
          return;
        }
        setVariantState({ options, loading: false, error: null });
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        setVariantState({ options: [], loading: false, error });
      });

    return () => {
      active = false;
    };
  }, [selectedMake, selectedModel]);

  React.useEffect(() => {
    if (!selectedMake || !selectedModel || !selectedVariant) {
      setEngineState({ options: [], loading: false, error: null });
      return;
    }

    let active = true;
    setEngineState({ options: [], loading: true, error: null });
    fetchVehicleEngines(selectedMake.code, selectedModel.code, selectedVariant.code)
      .then((options) => {
        if (!active) {
          return;
        }
        setEngineState({ options, loading: false, error: null });
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        setEngineState({ options: [], loading: false, error });
      });

    return () => {
      active = false;
    };
  }, [selectedMake, selectedModel, selectedVariant]);

  const stepStates = [makeState, modelState, variantState, engineState];
  const selectedPerStep = [selectedMake, selectedModel, selectedVariant, selectedEngine];

  const selectionComplete = Boolean(
    selectedMake && selectedModel && selectedVariant && selectedEngine
  );

  const handleSelectMake = (option) => {
    setSelectedMake({ ...option });
    setSelectedModel(null);
    setSelectedVariant(null);
    setSelectedEngine(null);
    setCurrentStep(1);
    onSelectionReset?.();
  };

  const handleSelectModel = (option) => {
    setSelectedModel({ ...option });
    setSelectedVariant(null);
    setSelectedEngine(null);
    setCurrentStep(2);
    onSelectionReset?.();
  };

  const handleSelectVariant = (option) => {
    setSelectedVariant({ ...option });
    setSelectedEngine(null);
    setCurrentStep(3);
    onSelectionReset?.();
  };

  const handleSelectEngine = (option) => {
    const normalized = { ...option };
    setSelectedEngine(normalized);
    setCurrentStep(3);
    if (selectedMake && selectedModel && selectedVariant) {
      const context = {
        type: "catalog-selection",
        lookupType: "VEHICLE_SELECTOR",
        make: selectedMake.name,
        makeCode: selectedMake.code,
        model: selectedModel.name,
        modelCode: selectedModel.code,
        modelYears: selectedModel.years ?? null,
        modelDescription: selectedModel.description ?? null,
        modelVariant: selectedVariant.name,
        variantCode: selectedVariant.code,
        variantBody: selectedVariant.body ?? null,
        variantYears: selectedVariant.years ?? null,
        engine: normalized.name,
        engineCode: normalized.code,
        fuel: normalized.fuel ?? null,
        powerKw: normalized.powerKw ?? null,
        powerPs: normalized.powerPs ?? null,
        displacement: normalized.displacement ?? null,
        ktype: normalized.ktype ?? null,
        vehicleKey: normalized.vehicleKey ?? null,
      };
      onVehicleSelected?.(context);
    }
  };

  const handleReset = () => {
    setSelectedMake(null);
    setSelectedModel(null);
    setSelectedVariant(null);
    setSelectedEngine(null);
    setModelState({ options: [], loading: false, error: null });
    setVariantState({ options: [], loading: false, error: null });
    setEngineState({ options: [], loading: false, error: null });
    setCurrentStep(0);
    onSelectionReset?.();
  };

  const breadcrumbs = STEP_CONFIG.map((step, index) => {
    const selection = selectedPerStep[index];
    const baseLabel = selection ? `${step.label}: ${selection.name}` : `${step.label} auswählen`;
    const clickable = index < currentStep && Boolean(selection);
    const color = index === currentStep ? "text.primary" : selection ? "text.secondary" : "text.disabled";
    return { ...step, label: baseLabel, clickable, color, index };
  });

  const handlers = [handleSelectMake, handleSelectModel, handleSelectVariant, handleSelectEngine];

  const renderStepContent = (stepIndex) => {
    const state = stepStates[stepIndex];
    const selected = selectedPerStep[stepIndex];

    if (state.loading) {
      return (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {[0, 1, 2].map((item) => (
            <Skeleton key={item} variant="rounded" height={52} animation="wave" />
          ))}
        </Stack>
      );
    }

    if (state.error) {
      return (
        <Alert severity="error" sx={{ mt: 1 }}>
          {state.error.message || "Fahrzeugdaten konnten nicht geladen werden."}
        </Alert>
      );
    }

    if (state.options.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {EMPTY_MESSAGES[stepIndex]}
        </Typography>
      );
    }

    const formatSecondary = SECONDARY_FORMATTERS[stepIndex];

    return (
      <List
        dense
        sx={{
          mt: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          maxHeight: 280,
          overflowY: "auto",
        }}
      >
        {state.options.map((option, idx) => {
          const secondary = formatSecondary ? formatSecondary(option) : null;
          const isSelected = selected?.code === option.code;
          return (
            <React.Fragment key={option.code}>
              <ListItemButton
                selected={isSelected}
                onClick={() => handlers[stepIndex](option)}
                alignItems="flex-start"
              >
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight={isSelected ? 600 : 500}>
                      {option.name}
                    </Typography>
                  }
                  secondary={secondary}
                  secondaryTypographyProps={{ variant: "body2" }}
                />
                {stepIndex === 3 && (
                  <Stack direction="row" spacing={1} sx={{ ml: 2, mt: 0.5 }}>
                    {option.vehicleKey && <Chip label="Vehicle Key" size="small" color="primary" variant="outlined" />}
                    {option.ktype && <Chip label={`K-Type ${option.ktype}`} size="small" variant="outlined" />}
                  </Stack>
                )}
              </ListItemButton>
              {idx < state.options.length - 1 && <Divider component="li" />}
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Breadcrumbs aria-label="Fahrzeugauswahl-Pfad">
          {breadcrumbs.map((crumb) =>
            crumb.clickable ? (
              <Link
                key={crumb.key}
                component="button"
                type="button"
                underline="hover"
                color="inherit"
                onClick={() => setCurrentStep(crumb.index)}
                sx={{ fontSize: "0.875rem" }}
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography key={crumb.key} color={crumb.color} sx={{ fontSize: "0.875rem" }}>
                {crumb.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
        <Button
          onClick={handleReset}
          startIcon={<RestartAltIcon />}
          size="small"
          disabled={!selectedMake && !selectedModel && !selectedVariant && !selectedEngine}
        >
          Zurücksetzen
        </Button>
      </Stack>

      <Typography variant="subtitle2" color="text.secondary">
        Schritt {currentStep + 1} von {STEP_CONFIG.length}: {STEP_CONFIG[currentStep].label} auswählen
      </Typography>

      {renderStepContent(currentStep)}

      {selectionComplete && (
        <Alert severity="success" sx={{ mt: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Fahrzeugkontext aktiv</Typography>
            <Typography variant="body2">
              {selectedMake.name} · {selectedModel.name} · {selectedVariant.name}
            </Typography>
            <Typography variant="body2">Motor: {selectedEngine.name}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {selectedEngine.vehicleKey && (
                <Chip label={`Vehicle Key ${selectedEngine.vehicleKey}`} size="small" color="primary" />
              )}
              {selectedEngine.ktype && <Chip label={`K-Type ${selectedEngine.ktype}`} size="small" color="secondary" />}
            </Stack>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
